/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” is a registered trademark of Frictionless Systems, LLC.
 */

package systems.frictionless.analytics.internal

import systems.frictionless.analytics.CrashAnalytics
import systems.frictionless.analytics.model.AnalyticsConfig

class SentryCrashAnalytics(config: AnalyticsConfig, gitCommitCount: Int, gitSha: String) : CrashAnalytics {
    init {
        Sentry.init(newInitParams(config, gitCommitCount, gitSha))
    }

    override fun reportCaughtException(throwable: Throwable) {
        Sentry.captureException(Error(throwable))
    }

    override fun setUserId(userId: String?) {
        Sentry.setUser(newUserParams(id = userId, username = null, email = null, segment = null, ipAddress = null))
    }
}

// Detekt doesn't work well when importing Javascript interfaces
// https://github.com/detekt/detekt/issues/4340
@JsModule("@sentry/browser")
@JsNonModule
@Suppress("UtilityClassWithPublicConstructor", "UnusedPrivateMember")
external class Sentry {
    companion object {
        fun init(sentryParams: SentryInitParams)

        fun setUser(userParams: SentryUserParams)

        fun captureException(error: Error)
    }
}

external interface SentryInitParams {
    val dsn: String
    val environment: String
    val release: String
}

// Detekt doesn't work well when importing Javascript interfaces
// https://github.com/detekt/detekt/issues/4340
@Suppress("UnusedPrivateMember")
private fun newInitParams(
    config: AnalyticsConfig,
    gitCommitCount: Int,
    gitSha: String
): SentryInitParams {
    val o = js("{}")
    o.dsn = config.apiKey
    o.release = "${config.project}-$gitCommitCount-$gitSha}"
    o.environment = config.environment.name.lowercase()

    // Note that tree shaking will remove debug code
    // See browser-app/webpack.config.d/sentry.js
    o.debug = config.isDebug

    @Suppress("UnsafeCastFromDynamic")
    return o
}

external interface SentryUserParams {
    val id: String?
    val username: String?
    val email: String?
    val segment: String?

    @Suppress("PropertyName", "ktlint:standard:property-naming", "VariableNaming")
    val ip_address: String?
}

// Detekt doesn't work well when importing Javascript interfaces
// https://github.com/detekt/detekt/issues/4340
@Suppress("UnusedPrivateMember")
private fun newUserParams(
    id: String?,
    username: String?,
    email: String?,
    segment: String?,
    ipAddress: String?
): SentryUserParams {
    val o = js("{}")
    o.id = id
    o.username = username
    o.email = email
    o.segment = segment
    o.ip_address = ipAddress

    @Suppress("UnsafeCastFromDynamic")
    return o
}
